import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../services/apiClient";
import "../styles/AdminSettingsPage.css";
import { useConfirm } from "../providers/ConfirmProvider";
import { useI18n } from "../providers/I18nProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mail servisi ayarları yönetim ekranı.
// Backend: /api/admin/mail-settings
//   GET  -> { effective, overrides, env_fallback }
//   PUT  -> alanları upsert eder (boş/null = DB'den sil, .env'e fallback)
//   DELETE -> tüm DB override'larını siler (her şey .env'e döner)
//   POST /test -> form değişiklikleri kaydedilmeden test gönderir (override body ile)

const EMPTY_FORM = {
  email_user: "",
  email_pass: "",
  smtp_host: "",
  smtp_port: "",
  smtp_secure: "",
  email_service: "",
  email_from: "",
  email_from_name: "",
};

const SourceBadge = ({ fromDb, hasEnv }) => {
  if (fromDb) return <span className="badge badge-primary">DB</span>;
  if (hasEnv) return <span className="badge">.env</span>;
  return <span className="badge badge-warning">tanımsız</span>;
};

const AdminMailSettingsPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [state, setState] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setStatusMsg("");
    try {
      const res = await apiClient.get("/admin/mail-settings");
      setState(res.data);
      // Form sadece DB'de override'lı alanları gösterir; geri kalan boş kalır
      // (placeholder etkili değeri belirtir). Bu sayede yanlışlıkla .env değerini
      // DB'ye yazma riski azalır.
      const eff = res.data?.effective || {};
      const ovr = res.data?.overrides || {};
      const loaded = {
        email_user: ovr.email_user ? String(eff.email_user || "") : "",
        email_pass: "", // şifre asla dolu gelmez
        smtp_host: ovr.smtp_host ? String(eff.smtp_host || "") : "",
        smtp_port: ovr.smtp_port && eff.smtp_port ? String(eff.smtp_port) : "",
        smtp_secure:
          ovr.smtp_secure && eff.smtp_secure !== null && eff.smtp_secure !== undefined
            ? (eff.smtp_secure ? "true" : "false")
            : "",
        email_service: ovr.email_service ? String(eff.email_service || "") : "",
        email_from: ovr.email_from ? String(eff.email_from || "") : "",
        email_from_name: ovr.email_from_name ? String(eff.email_from_name || "") : "",
      };
      setForm(loaded);
      setSavedForm(loaded);
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Mail ayarları alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm]);
  useUnsavedChanges(dirty);

  // Anlık doğrulama
  const fieldErrors = useMemo(() => {
    const out = {};
    const port = String(form.smtp_port || "").trim();
    if (port !== "") {
      const n = Number(port);
      if (!Number.isInteger(n) || n < 1 || n > 65535) out.smtp_port = t("adminMail.errors.port");
    }
    const from = String(form.email_from || "").trim();
    if (from !== "" && !EMAIL_RE.test(from)) out.email_from = t("adminMail.errors.email");
    const user = String(form.email_user || "").trim();
    if (user.includes("@") && !EMAIL_RE.test(user)) out.email_user = t("adminMail.errors.email");
    return out;
  }, [form, t]);

  const handleField = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Body'ye sadece "anlamlı" alanları koy. Boş string → null (sil/temizle).
  // smtp_secure boş → null; "true"/"false" → boolean.
  const buildPayload = () => {
    const p = {};
    for (const k of ["email_user", "smtp_host", "email_service", "email_from", "email_from_name"]) {
      p[k] = String(form[k] || "").trim() === "" ? null : String(form[k]).trim();
    }
    if (String(form.smtp_port || "").trim() === "") {
      p.smtp_port = null;
    } else {
      p.smtp_port = parseInt(String(form.smtp_port), 10);
    }
    if (form.smtp_secure === "" || form.smtp_secure === null) {
      p.smtp_secure = null;
    } else {
      p.smtp_secure = form.smtp_secure === true || form.smtp_secure === "true";
    }
    // Şifre boşsa GÖNDERME → mevcut DB değeri korunur. Açıkça temizlemek için
    // "Şifreyi sıfırla" düğmesi var.
    if (String(form.email_pass || "") !== "") {
      p.email_pass = String(form.email_pass);
    }
    return p;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (Object.keys(fieldErrors).length > 0) {
      setStatusMsg(t("adminMail.fixErrors"));
      return;
    }
    setSaving(true);
    setStatusMsg("");
    try {
      const res = await apiClient.put("/admin/mail-settings", buildPayload());
      setState(res.data);
      setStatusMsg("Kaydedildi.");
      // Şifre alanını temizle (artık DB'de)
      const cleared = { ...form, email_pass: "" };
      setForm(cleared);
      setSavedForm(cleared);
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const handleClearPassword = async () => {
    const ok = await confirm(
      "DB'deki SMTP şifresi silinsin mi? .env'deki EMAIL_PASS varsa ona geri dönülür."
    );
    if (!ok) return;
    setSaving(true);
    try {
      const res = await apiClient.put("/admin/mail-settings", { email_pass: null });
      setState(res.data);
      setForm((prev) => ({ ...prev, email_pass: "" }));
      setStatusMsg("DB'deki şifre silindi.");
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Şifre silinemedi.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    const ok = await confirm(
      "Tüm mail ayarları DB'den silinsin mi? Sistem tamamen .env değerlerine geri döner."
    );
    if (!ok) return;
    setSaving(true);
    try {
      const res = await apiClient.delete("/admin/mail-settings");
      setState(res.data);
      setForm(EMPTY_FORM);
      setStatusMsg(".env'e geri dönüldü.");
    } catch (err) {
      setStatusMsg(err.response?.data?.msg || "Sıfırlanamadı.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (e) => {
    e?.preventDefault?.();
    if (testing) return;
    const to = String(testTo || "").trim();
    if (!to) {
      setTestResult({ ok: false, msg: "Test alıcısı zorunludur." });
      return;
    }
    if (!EMAIL_RE.test(to)) {
      setTestResult({ ok: false, msg: t("adminMail.errors.email") });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      // Override: form'da girilen ama henüz kaydedilmemiş değerlerle test et.
      const override = buildPayload();
      // Null alanları override'dan çıkar (override null = "bu alanı kaydedilenin
      // üstüne yazma"; istenmeyen davranış).
      const cleanOverride = {};
      for (const [k, v] of Object.entries(override)) {
        if (v !== null && v !== undefined && v !== "") cleanOverride[k] = v;
      }
      const res = await apiClient.post("/admin/mail-settings/test", {
        to,
        override: cleanOverride,
      });
      setTestResult({ ok: true, ...res.data });
    } catch (err) {
      setTestResult({
        ok: false,
        msg: err.response?.data?.msg || err.message || "Test başarısız.",
        code: err.response?.data?.code,
      });
    } finally {
      setTesting(false);
    }
  };

  const eff = state?.effective || {};
  const ovr = state?.overrides || {};
  const envF = state?.env_fallback || {};

  return (
    <div className="admin-settings-page">
      <div className="page-head">
        <div>
          <h2>Mail Servisi</h2>
          <p className="muted">
            Sistemin gönderdiği bildirim ve parola sıfırlama e-postalarının
            altyapısı. DB'de bir değer varsa o kullanılır; yoksa <code>.env</code>'e
            fallback yapılır. Bir alanı boş kaydederseniz DB değeri silinir ve
            sistem o alan için <code>.env</code>'e geri döner.
          </p>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleResetAll}
            disabled={saving || loading}
            title="Tüm DB override'larını siler, sistem .env'e geri döner"
          >
            .env'e Geri Dön
          </button>
        </div>
      </div>

      {statusMsg && (
        <div className="status-banner" role="status">
          {statusMsg}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: 16 }}>Yükleniyor…</div>
      ) : (
        <>
          <form className="card form-card" onSubmit={handleSave}>
            <h3>Aktif Yapılandırma</h3>
            <p className="muted small">
              "Kaynak" sütunu, sistemin hangi alanı nereden okuduğunu gösterir.
              Form'daki bir alanı doldurup kaydederseniz DB öncelikli olur.
            </p>

            <div className="form-grid">
              <div className="form-row">
                <label>EMAIL_USER</label>
                <input
                  type="text"
                  value={form.email_user}
                  onChange={handleField("email_user")}
                  placeholder={!ovr.email_user && eff.email_user ? `(.env: ${eff.email_user})` : ""}
                />
                {fieldErrors.email_user && <div className="field-error" role="alert">{fieldErrors.email_user}</div>}
                <div className="meta">
                  <SourceBadge fromDb={ovr.email_user} hasEnv={envF.email_user} />
                  <span className="muted small">Etkili: {eff.email_user || "—"}</span>
                </div>
              </div>

              <div className="form-row">
                <label>EMAIL_PASS</label>
                <input
                  type="password"
                  value={form.email_pass}
                  onChange={handleField("email_pass")}
                  autoComplete="new-password"
                  placeholder={
                    ovr.email_pass
                      ? "(DB'de şifreli kayıtlı — değiştirmek için yeni şifre yazın)"
                      : envF.email_pass
                        ? "(.env'de tanımlı)"
                        : "(tanımsız)"
                  }
                />
                <div className="meta">
                  <SourceBadge fromDb={ovr.email_pass} hasEnv={envF.email_pass} />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClearPassword}
                    disabled={!ovr.email_pass || saving}
                    style={{ marginLeft: 8 }}
                  >
                    DB'deki şifreyi sil
                  </button>
                </div>
              </div>

              <div className="form-row">
                <label>SMTP_HOST</label>
                <input
                  type="text"
                  value={form.smtp_host}
                  onChange={handleField("smtp_host")}
                  placeholder={!ovr.smtp_host && eff.smtp_host ? `(.env: ${eff.smtp_host})` : ""}
                />
                <div className="meta">
                  <SourceBadge fromDb={ovr.smtp_host} hasEnv={envF.smtp_host} />
                  <span className="muted small">Etkili: {eff.smtp_host || "—"}</span>
                </div>
              </div>

              <div className="form-row">
                <label>SMTP_PORT</label>
                <input
                  type="number"
                  min="1"
                  max="65535"
                  value={form.smtp_port}
                  onChange={handleField("smtp_port")}
                  placeholder={!ovr.smtp_port && eff.smtp_port ? `(.env: ${eff.smtp_port})` : "587"}
                />
                {fieldErrors.smtp_port && <div className="field-error" role="alert">{fieldErrors.smtp_port}</div>}
                <div className="meta">
                  <SourceBadge fromDb={ovr.smtp_port} hasEnv={envF.smtp_port} />
                  <span className="muted small">Etkili: {eff.smtp_port ?? "—"}</span>
                </div>
              </div>

              <div className="form-row">
                <label>SMTP_SECURE</label>
                <select value={form.smtp_secure} onChange={handleField("smtp_secure")}>
                  <option value="">(boş — .env veya porta göre otomatik)</option>
                  <option value="true">true (TLS direkt — genelde 465)</option>
                  <option value="false">false (STARTTLS — genelde 587)</option>
                </select>
                <div className="meta">
                  <SourceBadge fromDb={ovr.smtp_secure} hasEnv={envF.smtp_secure} />
                  <span className="muted small">
                    Etkili: {eff.smtp_secure === null || eff.smtp_secure === undefined ? "—" : String(eff.smtp_secure)}
                  </span>
                </div>
              </div>

              <div className="form-row">
                <label>EMAIL_SERVICE</label>
                <input
                  type="text"
                  value={form.email_service}
                  onChange={handleField("email_service")}
                  placeholder={
                    !ovr.email_service && eff.email_service
                      ? `(.env: ${eff.email_service})`
                      : "(yalnızca SMTP_HOST yokken kullanılır — ör: gmail)"
                  }
                />
                <div className="meta">
                  <SourceBadge fromDb={ovr.email_service} hasEnv={envF.email_service} />
                  <span className="muted small">Etkili: {eff.email_service || "—"}</span>
                </div>
              </div>

              <div className="form-row">
                <label>Gönderen Adresi (EMAIL_FROM)</label>
                <input
                  type="email"
                  value={form.email_from}
                  onChange={handleField("email_from")}
                  placeholder={
                    !ovr.email_from && eff.email_from
                      ? `(.env veya EMAIL_USER: ${eff.email_from})`
                      : "ornek: iys@bozankayateknoloji.com (boş = EMAIL_USER)"
                  }
                />
                {fieldErrors.email_from && <div className="field-error" role="alert">{fieldErrors.email_from}</div>}
                <div className="meta">
                  <SourceBadge fromDb={ovr.email_from} hasEnv={envF.email_from} />
                  <span className="muted small">Etkili: {eff.email_from || "—"}</span>
                </div>
                <div className="muted small" style={{ marginTop: 4 }}>
                  Yalnızca e-posta adresi yazın. Display name için aşağıdaki "Gönderen Adı" alanını kullanın.
                </div>
              </div>

              <div className="form-row">
                <label>Gönderen Adı (display name)</label>
                <input
                  type="text"
                  value={form.email_from_name}
                  onChange={handleField("email_from_name")}
                  placeholder={
                    !ovr.email_from_name && eff.email_from_name
                      ? `(.env: ${eff.email_from_name})`
                      : "ornek: İzin Yönetim Sistemi"
                  }
                />
                <div className="meta">
                  <SourceBadge fromDb={ovr.email_from_name} hasEnv={envF.email_from_name} />
                  <span className="muted small">Etkili: {eff.email_from_name || "—"}</span>
                </div>
              </div>

              <div className="form-row">
                <label>From header önizleme</label>
                <input type="text" value={eff.from_header || ""} readOnly disabled />
                <div className="muted small">
                  Alıcının e-posta istemcisinde gönderen olarak bu görünür. Display name yoksa
                  "[Unknown]" gibi davranabilir.
                </div>
              </div>
            </div>

            <div className="settings-actions" style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Kaydediliyor…" : "Kaydet"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={load} disabled={saving}>
                Geri Yükle
              </button>
              {dirty && <span className="dirty-note">{t("adminSettings.unsaved")}</span>}
            </div>
          </form>

          <form className="card form-card" style={{ marginTop: 16 }} onSubmit={handleTest}>
            <h3>Test E-postası</h3>
            <p className="muted small">
              Formdaki son değerlerle (kaydetmeden) bir test e-postası gönderir.
              Form boş bırakılan alanlar için sistem geçerli yapılandırmayı (DB / .env) kullanır.
              Şifreyi formda doldurmadıysanız mevcut kaydedilmiş/env şifre ile test edilir.
            </p>
            <div className="form-grid">
              <div className="form-row">
                <label>Hedef e-posta</label>
                <input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="ornek@firma.com"
                  required
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={testing}>
                {testing ? "Gönderiliyor…" : "Test Gönder"}
              </button>
            </div>
            {testResult && (
              <div
                className="status-banner"
                style={{
                  marginTop: 12,
                  background: testResult.ok ? "var(--success-bg, #e6f4ea)" : "var(--danger-bg, #fdecea)",
                }}
              >
                {testResult.ok ? (
                  <>
                    ✓ Test başarılı.
                    {testResult.messageId && (
                      <span className="muted small"> messageId: {testResult.messageId}</span>
                    )}
                  </>
                ) : (
                  <>
                    ✗ {testResult.msg || "Test başarısız."}
                    {testResult.code && <span className="muted small"> ({testResult.code})</span>}
                  </>
                )}
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default AdminMailSettingsPage;
